import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ga, gspx } from '@rsos/capstone';
import ExternalLinkIcon from '@rsos/assets/icons/src/ExternalLinkIcon';
import Box from '../../../base/Box';
import Text from '../../../base/Text';
import { SOURCE_911INFORM } from '../SpatialLayers/spatialLayersSources';
import {
  InfoWindowConfirmationContainer,
  InfoWindowConfirmationTitle,
  InfoWindowConfirmationText,
  InfoWindowCancelButton,
  InfoWindowConfirmButton,
  InfoWindowButtonContainer,
} from './InfoWindow.styles';
import { InfoWindowWarning } from './InfoWindowWarning';

export const STATUS_ACTIVE = 'active';
export const STATUS_INACTIVE = 'inactive';

// Note: This component cannot dynamically pick up on the theme because it is
// rendered directly into the DOM by event handlers for Google Maps. Theme
// support will have to be done separately from feature parity

const InfoWindowContainer = styled.div`
  width: 210px;
`;

const InfoWindowLabel = styled(Text).attrs({
  color: '#c0c0c0',
  size: 'xsmall',
})`
  letter-spacing: normal;
`;

const InfoWindowTitle = styled(Text).attrs({
  color: '#636363',
  size: 'normal',
})`
  letter-spacing: normal;
`;
const InfoWindowSubTitle = styled(Text).attrs({
  color: '#636363',
  size: 'small',
})`
  letter-spacing: normal;
`;
const InfoWindowButton = styled.button`
  background-color: #29abe0;
  color: #fff;
  cursor: pointer;
  display: flex;
  justify-content: center;
  font-size: 12px;
  padding: 6.5px;
  outline: 0;
  border: none;
  border-radius: 4px;
  min-width: 190px;
  margin: 10px auto 0;
  &:hover {
    background: #015983;
  }
`;

/**
 * InfoWindow is an uncontrolled component used within the Google Map which
 * means that it runs outside of the application's lifecycle and does not have
 * access to the store nor redux's context provider. All data dependencies
 * should either be passed as a prop or through a callback.
 * @param {Object} details
 * @param {string} status
 * @param {Function} onRequestDeepLink
 */
const InfoWindow = ({ details, status, onRequestDeepLink }) => {
  const mounted = useRef(false);
  const [isConfirmationContainerOpened, toggleConfirmationContainer] =
    useState(false);
  const [requestedDeepLink, setRequestedDeepLink] = useState({
    url: '',
    creating: false,
  });
  const { address, deepLink, name, source } = details;
  const tooltipText =
    'View floor plans, control access points, and video cameras';

  const open911InformTab = link => {
    gspx.trackCustomEvent('Data Map', {
      name: `Click on 911 Inform link`,
      Category: 'Google Map',
      'Launched date': new Date(),
    });
    window.open(link, '_blank', 'noopener noreferrer');
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  let displayAddress = `${address && address.street_address}, `;

  if (address && address.street_address_2) {
    displayAddress = displayAddress + `${address.street_address_2}, `;
  }

  displayAddress =
    displayAddress +
    `${address.locality}, ${address.region}, ${address.country_code}, ${address.postal_code}`;

  const handleAccessBuilding = () => {
    if (status === STATUS_ACTIVE && deepLink) {
      open911InformTab(deepLink);
    } else {
      setRequestedDeepLink({ creating: true, url: '' });
      onRequestDeepLink(details, ({ url }) => {
        if (mounted.current) {
          setRequestedDeepLink({ creating: false, url });
          open911InformTab(url);
        }
      });
    }
  };

  const handleConfirmation = () => {
    toggleConfirmationContainer(prev => !prev);
  };

  if (isConfirmationContainerOpened)
    return (
      <InfoWindowConfirmationContainer onMouseLeave={handleConfirmation}>
        <InfoWindowConfirmationTitle>
          Are you sure there is an emergency?
        </InfoWindowConfirmationTitle>
        <InfoWindowConfirmationText>
          Accessing the building will launch 911inform and notify their
          subscribers of a potential emergency.
        </InfoWindowConfirmationText>
        <InfoWindowButtonContainer horizontal>
          <InfoWindowCancelButton onClick={handleConfirmation}>
            No, Cancel
          </InfoWindowCancelButton>

          <InfoWindowConfirmButton onClick={handleAccessBuilding}>
            Yes, Confirmed
            <Box pl={1}>
              <ExternalLinkIcon width={12} height={12} />
            </Box>
          </InfoWindowConfirmButton>
        </InfoWindowButtonContainer>
      </InfoWindowConfirmationContainer>
    );

  return (
    <InfoWindowContainer>
      {source !== SOURCE_911INFORM ? (
        <>
          <InfoWindowTitle>{name}</InfoWindowTitle>
          <InfoWindowSubTitle>{displayAddress}</InfoWindowSubTitle>
        </>
      ) : (
        <Box margin={'0 auto'} py={2}>
          <Box pl="10px">
            <InfoWindowLabel>
              {source} {status === STATUS_INACTIVE ? ' | inactive' : ''}
            </InfoWindowLabel>
            <InfoWindowTitle>{name}</InfoWindowTitle>
            <InfoWindowSubTitle>{displayAddress}</InfoWindowSubTitle>
            <InfoWindowWarning />
          </Box>
          <div title={tooltipText}>
            <InfoWindowButton
              disabled={requestedDeepLink.creating}
              isLoading={requestedDeepLink.creating}
              onClick={handleConfirmation}
            >
              Access Building
            </InfoWindowButton>
          </div>
        </Box>
      )}
    </InfoWindowContainer>
  );
};

InfoWindow.propTypes = {
  details: PropTypes.shape({
    address: PropTypes.object,
    name: PropTypes.string,
    source: PropTypes.string,
  }),
  status: PropTypes.string,
};

export default InfoWindow;
